import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { clearPrescription } from '../store/prescriptionSlice';
import Navbar from '../Components/Navbar';
import Footer from '../Components/Footer';
import EyePowerFinder from '../Components/EyePowerFinder';

function SavedPrescriptionBanner() {
  const prescription = useSelector((s) => s.prescription);
  const dispatch = useDispatch();

  if (!prescription.isSet) return null;

  const fmt = (n) => {
    if (n === null || n === undefined) return '—';
    if (n === 0) return 'Plano';
    return `${n > 0 ? '+' : ''}${Math.abs(n).toFixed(2)}`;
  };

  const rSph = fmt(prescription.rightEye?.effectiveSph ?? prescription.rightEye?.sph);
  const lSph = fmt(prescription.leftEye?.effectiveSph  ?? prescription.leftEye?.sph);

  return (
    <div className="mb-6 rounded-xl bg-white border border-sand shadow-card px-4 py-3 flex items-center gap-3 flex-wrap">
      <div className="flex items-center gap-2 text-clay shrink-0">
        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <circle cx="12" cy="12" r="3"/><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"/>
        </svg>
        <span className="text-xs font-semibold text-clay uppercase tracking-wide">Last saved</span>
      </div>
      <div className="flex items-center gap-2 flex-1 flex-wrap">
        <span className="text-sm font-mono font-bold text-ink">
          OD {rSph}
        </span>
        <span className="text-sand">·</span>
        <span className="text-sm font-mono font-bold text-ink">
          OS {lSph}
        </span>
        {prescription.condition && prescription.condition !== 'none' && (
          <span className="text-xs bg-brand-50 text-brand-700 px-2 py-0.5 rounded-full">
            {prescription.condition}
          </span>
        )}
      </div>
      <button
        onClick={() => dispatch(clearPrescription())}
        className="text-xs text-clay hover:text-red-500 transition-colors shrink-0"
      >
        Clear
      </button>
    </div>
  );
}

export default function EyePowerFinderPage() {
  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <Navbar />
      <main className="flex-1 py-10 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Page heading */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-ink mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7 text-cream" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3" />
                <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
              </svg>
            </div>
            <h1 className="text-3xl font-display font-normal tracking-tight text-ink">Eye Power Calculator</h1>
            <p className="mt-2 text-clay max-w-md mx-auto">
              Enter your prescription to see your contact lens power.
              Works with sphere-based prescriptions for colored and clear lenses.
            </p>
          </div>

          {/* Saved prescription banner — visible immediately, no scrolling needed */}
          <SavedPrescriptionBanner />

          <EyePowerFinder />

          {/* Explainer */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <InfoCard
              icon="🔍"
              title="What is SPH?"
              body="Sphere corrects near or farsightedness. Negative values = myopia, positive = hyperopia."
            />
            <InfoCard
              icon="⚕️"
              title="What is CYL / Axis?"
              body="Cylinder and Axis correct astigmatism. Most colored lenses are spherical only."
            />
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function InfoCard({ icon, title, body }) {
  return (
    <div className="bg-white rounded-xl border border-sand shadow-card p-4">
      <div className="text-2xl mb-2">{icon}</div>
      <h3 className="font-semibold text-ink text-sm mb-1">{title}</h3>
      <p className="text-xs text-clay leading-relaxed">{body}</p>
    </div>
  );
}
